import React from 'react';
import { Card, Button, Space, Typography, Tag, Divider } from 'antd';
import { Edit, ArrowLeft, Calendar, Hash } from 'lucide-react';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CodeBlock from './CodeBlock';
// 采用与桌面版一致的代码高亮、行号与复制按钮
import { Document } from '../types';
import { AppSettings } from '../utils/SettingsManager';

const { Title, Text } = Typography;

interface NotePreviewProps {
  document: Document;
  onEdit: () => void;
  onBack: () => void;
  settings: AppSettings;
}

const NotePreview: React.FC<NotePreviewProps> = ({
  document,
  onEdit,
  onBack,
  settings
}) => {
  return (
    <div className="editor-container">
      <Card>
        {/* 工具栏 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          padding: '0 0 16px 0',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Button
              icon={<ArrowLeft size={16} />}
              onClick={onBack}
              title="返回列表"
            >
              返回
            </Button>
            <Title level={4} style={{ margin: 0 }}>
              预览模式
            </Title>
          </div>
          <Space>
            <Button
              type="primary"
              icon={<Edit size={16} />}
              onClick={onEdit}
              title="编辑文档"
            >
              编辑
            </Button>
          </Space>
        </div>

        {/* 文档信息 */}
        <div style={{ marginBottom: 24 }}>
          <Title level={2} style={{ marginBottom: 16 }}>
            {document.title}
          </Title>

          <Space wrap style={{ marginBottom: 16 }}>
            {/* 显示标签信息 */}
            {(document.tag || (document.tags && document.tags.length > 0)) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Hash size={14} />
                {document.tag ? (
                  <Tag color={document.tag.color || 'blue'}>
                    {document.tag.name}
                  </Tag>
                ) : (
                  <Space wrap>
                    {document.tags?.map((tag, index) => (
                      <Tag key={index} color="blue">
                        {tag}
                      </Tag>
                    ))}
                  </Space>
                )}
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Calendar size={14} />
              <Text type="secondary">
                创建于 {format(new Date(document.createdAt), 'yyyy-MM-dd HH:mm')}
              </Text>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Calendar size={14} />
              <Text type="secondary">
                更新于 {format(new Date(document.updatedAt), 'yyyy-MM-dd HH:mm')}
              </Text>
            </div>
          </Space>

          <Divider />
        </div>

        {/* 内容预览 */}
        <div style={{
          minHeight: 'calc(100vh - 400px)',
          padding: '16px',
          border: '1px solid #f0f0f0',
          borderRadius: '6px',
          backgroundColor: settings.darkMode ? '#1f1f1f' : '#fff'
        }}>
          {document.content ? (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => (
                  <h1 style={{
                    borderBottom: '2px solid #eee',
                    paddingBottom: '8px',
                    marginTop: '24px',
                    marginBottom: '16px',
                    color: settings.darkMode ? '#fff' : '#262626'
                  }}>
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 style={{
                    borderBottom: '1px solid #eee',
                    paddingBottom: '4px',
                    marginTop: '24px',
                    marginBottom: '16px',
                    color: settings.darkMode ? '#fff' : '#262626'
                  }}>
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 style={{
                    marginTop: '20px',
                    marginBottom: '12px',
                    color: settings.darkMode ? '#fff' : '#262626'
                  }}>
                    {children}
                  </h3>
                ),
                p: ({ children }) => (
                  <p style={{
                    marginBottom: '16px',
                    lineHeight: '1.6',
                    color: settings.darkMode ? '#fff' : '#262626'
                  }}>
                    {children}
                  </p>
                ),
                code: ({ inline, className, children, ...rest }: any) => (
                  <CodeBlock inline={inline} className={className} settings={settings} {...rest}>
                    {children}
                  </CodeBlock>
                ),
                blockquote: ({ children }) => (
                  <blockquote style={{
                    borderLeft: `4px solid ${settings.darkMode ? '#434343' : '#dfe2e5'}`,
                    paddingLeft: '16px',
                    margin: '16px 0',
                    color: settings.darkMode ? '#8b949e' : '#6a737d',
                    fontStyle: 'italic'
                  }}>
                    {children}
                  </blockquote>
                ),
                table: ({ children }) => (
                  <table style={{
                    borderCollapse: 'collapse',
                    width: '100%',
                    margin: '16px 0',
                    border: `1px solid ${settings.darkMode ? '#434343' : '#dfe2e5'}`
                  }}>
                    {children}
                  </table>
                ),
                th: ({ children }) => (
                  <th style={{
                    border: `1px solid ${settings.darkMode ? '#434343' : '#dfe2e5'}`,
                    padding: '8px 12px',
                    background: settings.darkMode ? '#2d2d2d' : '#f6f8fa',
                    color: settings.darkMode ? '#fff' : '#262626',
                    fontWeight: 600,
                    textAlign: 'left'
                  }}>
                    {children}
                  </th>
                ),
                td: ({ children }) => (
                  <td style={{
                    border: `1px solid ${settings.darkMode ? '#434343' : '#dfe2e5'}`,
                    padding: '8px 12px',
                    color: settings.darkMode ? '#fff' : '#262626'
                  }}>
                    {children}
                  </td>
                ),
                ul: ({ children }) => (
                  <ul style={{
                    paddingLeft: '20px',
                    marginBottom: '16px',
                    color: settings.darkMode ? '#fff' : '#262626'
                  }}>
                    {children}
                  </ul>
                ),
                ol: ({ children }) => (
                  <ol style={{
                    paddingLeft: '20px',
                    marginBottom: '16px',
                    color: settings.darkMode ? '#fff' : '#262626'
                  }}>
                    {children}
                  </ol>
                ),
                li: ({ children }) => (
                  <li style={{
                    marginBottom: '4px',
                    lineHeight: '1.6'
                  }}>
                    {children}
                  </li>
                ),
                a: ({ children, href }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#1890ff',
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.textDecoration = 'none';
                    }}
                  >
                    {children}
                  </a>
                )
              }}
            >
              {document.content}
            </ReactMarkdown>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: settings.darkMode ? '#8c8c8c' : '#999'
            }}>
              <Text type="secondary">此文档暂无内容</Text>
            </div>
          )}
        </div>

        {/* 底部信息 */}
        <div style={{
          marginTop: 16,
          padding: '8px 0',
          borderTop: '1px solid #f0f0f0',
          fontSize: '12px',
          color: '#8c8c8c',
          textAlign: 'center'
        }}>
          <Space split={<span>•</span>}>
            <span>字数: {document.content.length}</span>
            <span>预计阅读时间: {Math.max(1, Math.ceil(document.content.length / 500))} 分钟</span>
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default NotePreview;
